import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Field from '../../components/forms/Field';
import {
  emailFormStructure,
  runnerFormStructure,
  emergencyFormStructure,
} from '../../data/registrationStructure';
import { applyVisibility } from '../../utils/formVisibility';
import { z } from 'zod';

import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { zodResolver } from '@hookform/resolvers/zod';
import { registrationSchema } from '../../validation/registrationSchema';
import { readJson } from '../../utils/generateToken';
import RegStatusDialog from '../RegCompletePopup/RegStatusDialog';

const SUMMARY_PATH = '/event/register/indomaret/summary';

const RADIUS_PX = 12;
const SECTION_BORDER = '#D1D5DB';

const STATIC_SIZE_OPTIONS =
  runnerFormStructure.find((field) => field.key === 'size')?.options || [];

function SizeChartDialog({ open, onClose, imgSrc, title = 'Size Chart' }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            maxHeight: {
              xs: 'calc(100dvh - 160px)',
              sm: 'calc(100dvh - 180px)',
            },
            overflow: 'auto',
          }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt="Size chart"
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
              Size chart image is not available.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SizeOption = z.object({
  value: z.string(),
  label: z.string(),
});

const SizeOptionLoose = z.union([
  SizeOption,
  z.string().length(0),
  z.null(),
  z.undefined(),
]);

/**
 * Normalize size limit menjadi bentuk yang bisa langsung
 * dipakai Field select.
 *
 * Support:
 *
 * {
 *   label: 'M',
 *   limit: 443,
 *   used: 100,
 *   remaining: 343
 * }
 *
 * atau:
 *
 * {
 *   label: 'M',
 *   limit: 443,
 *   used: 100
 * }
 *
 * Kalau remaining tidak ada:
 * remaining = limit - used
 */
const normalizeLimitOptions = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => item?.label)
    .map((item) => {
      const limit = Number(item.limit) || 0;
      const used = Number(item.used) || 0;

      const remaining =
        item.remaining !== undefined && item.remaining !== null
          ? Math.max(0, Number(item.remaining) || 0)
          : Math.max(0, limit - used);

      const label = String(item.label).trim();

      return {
        value: label,
        label,
        limit,
        used,
        remaining,
        disabled: remaining <= 0,
      };
    });
};

/**
 * Validator reusable untuk:
 * - jersey
 * - belt
 * - finisher
 */
const createSizeValidator = (
  options,
  { required = false, requiredMessage = 'Size wajib dipilih', soldOutMessage },
) =>
  SizeOptionLoose.superRefine((opt, issue) => {
    if (!opt || typeof opt === 'string') {
      if (required) {
        issue.addIssue({
          code: 'custom',
          message: requiredMessage,
        });
      }

      return;
    }

    const selected = (options || []).find(
      (item) => String(item.value) === String(opt.value),
    );

    if (!selected) {
      issue.addIssue({
        code: 'custom',
        message: 'Size tidak tersedia/invalid',
      });

      return;
    }

    if (selected.disabled) {
      issue.addIssue({
        code: 'custom',
        message: soldOutMessage || 'Size ini sudah habis',
      });
    }
  });

const isISO_YMD = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const toUTCDate = (s) => new Date(`${s}T00:00:00Z`);

const fmtDMY = (iso) => {
  const [y, m, d] = iso.split('-');

  return `${d}-${m}-${y}`;
};

const normalizeRaceNameToEnum = (nameOrId) => {
  if (typeof nameOrId === 'string') {
    const s = nameOrId.trim().toLowerCase();

    if (s.includes('5')) return '5k';
    if (s.includes('10')) return '10k';
    if (s.includes('21')) return '21k';
  }

  if (typeof nameOrId === 'number') {
    if (nameOrId === 229) return '5k';
    if (nameOrId === 231) return '21k';
    if (nameOrId === 230) return '10k';
  }

  return undefined;
};

const isoDateOnly = (dLike) => {
  try {
    if (!dLike) return undefined;

    const d = new Date(dLike);

    if (Number.isNaN(d.getTime())) {
      return undefined;
    }

    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
};

function gridPropsFromWidth(width) {
  let cols = 12;

  if (width === true || width == null || width === '1') {
    cols = 12;
  } else if (typeof width === 'number') {
    cols = Math.max(1, Math.min(12, width));
  } else if (typeof width === 'string' && width.includes('/')) {
    const [n, d] = width.split('/').map(parseFloat);

    if (Number.isFinite(n) && Number.isFinite(d) && d > 0) {
      cols = Math.round((n / d) * 12);
    }
  }

  return {
    xs: 12,
    sm: 12,
    md: cols,
    lg: cols,
  };
}

function Section({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${RADIUS_PX}px`,
        borderColor: SECTION_BORDER,
        borderWidth: 3,
        p: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 2,
        }}
      >
        {title}
      </Typography>

      {children}
    </Paper>
  );
}

const getSelectedSizeValue = (value) => {
  if (!value) return '';

  if (typeof value === 'object') {
    return value.value ?? '';
  }

  return String(value);
};

const validateSizeStock = ({
  value,
  options,
  required = false,
  requiredMessage = 'Size wajib dipilih',
  soldOutMessage = 'Size ini sudah habis',
}) => {
  const selectedValue = getSelectedSizeValue(value);

  if (!selectedValue) {
    if (required) {
      return requiredMessage;
    }

    return null;
  }

  const selected = (options || []).find(
    (item) => String(item.value) === String(selectedValue),
  );

  if (!selected) {
    return 'Size tidak tersedia/invalid';
  }

  if (selected.disabled) {
    return soldOutMessage;
  }

  return null;
};

const applyForcedSoldOut = (options = [], soldOutValue) => {
  const soldOutArray = Array.isArray(soldOutValue)
    ? soldOutValue
    : [soldOutValue].filter(Boolean);

  return options.map((item) => {
    const isForcedSoldOut = soldOutArray.some(
      (size) => String(size) === String(item.value),
    );

    return {
      ...item,
      disabled: !!item.disabled || isForcedSoldOut,
      remaining: isForcedSoldOut ? 0 : item.remaining,
    };
  });
};

export default function IndomaretRegistration() {
  const navigate = useNavigate();

  const { state } = useLocation();

  const { item } = useParams();

  // ========================================
  // EDIT MODE
  // ========================================

  const initialValues = state?.initialValues || null;

  const meta = state?.meta || null;

  const stockErrors = meta?.stockErrors || {};
  const soldOutSizes = meta?.soldOutSizes || {};

  const [stockDialog, setStockDialog] = useState({
    open: false,
    title: '',
    message: '',
  });

  useEffect(() => {
    const messages = [
      stockErrors.size,
      stockErrors.belt_size,
      stockErrors.finisher_size,
    ].filter(Boolean);

    if (messages.length === 0) return;

    setStockDialog({
      open: true,
      title: 'Size Tidak Tersedia',
      message: messages.join('\n'),
    });
  }, [stockErrors.size, stockErrors.belt_size, stockErrors.finisher_size]);

  // ========================================
  // DECODE PARAM
  // ========================================

  const [ev, setEv] = useState(null);

  const [decodeError, setDecodeError] = useState(null);

  // ========================================
  // REGION
  // ========================================

  const [regionData, setRegionData] = useState([]);

  const [regionsLoading, setRegionsLoading] = useState(true);

  const [regionsError, setRegionsError] = useState(null);

  useEffect(() => {
    let active = true;

    const loadRegions = async () => {
      try {
        setRegionsLoading(true);
        setRegionsError(null);

        const response = await fetch('/data/indonesia-regions.json');

        if (!response.ok) {
          throw new Error('Gagal memuat data wilayah');
        }

        const result = await response.json();

        if (!active) return;

        setRegionData(Array.isArray(result) ? result : []);
      } catch (error) {
        if (!active) return;

        setRegionData([]);
        setRegionsError(error);
      } finally {
        if (active) {
          setRegionsLoading(false);
        }
      }
    };

    loadRegions();

    return () => {
      active = false;
    };
  }, []);

  // ========================================
  // DECODE URL PARAM
  // ========================================

  useEffect(() => {
    if (initialValues || meta) return;
    if (!item) return;

    try {
      const fromRead = JSON.parse(readJson(item));

      setEv(fromRead);

      setDecodeError(null);
    } catch {
      setDecodeError('Data registrasi tidak valid. Silakan buka link ulang.');
    }
  }, [item, initialValues, meta]);

  // ========================================
  // SIZE CHART
  // ========================================

  const [sizeChartOpen, setSizeChartOpen] = useState(false);

  const [selectedChart, setSelectedChart] = useState('jersey');

  const sizeChartImgSrc = '/jersey_size.jpeg';

  const beltChartImgSrc = '/belt_size.jpeg';

  const jacketChartImgSrc = '/jakcet_size.jpeg';

  // ========================================
  // EVENT & CATEGORY
  // ========================================

  const eventId = meta?.eventId ?? ev?.eventId ?? null;

  const categoryId = meta?.category_id ?? ev?.category?.race_category ?? null;

  // ========================================
  // SIZE LIMIT DATA
  // ========================================

  /**
   * First entry:
   * data dari decoded URL (`ev`)
   *
   * Kalau balik dari summary/edit:
   * data disimpan di `meta`
   */

  const jerseyLimitData = meta?.jerseyLimit ?? ev?.jerseyLimit ?? null;

  const beltLimitData = meta?.beltLimit ?? ev?.beltLimit ?? [];

  const jacketLimitData = meta?.jacketLimit ?? ev?.jacketLimit ?? [];

  /**
   * jerseyLimit bentuknya:
   *
   * {
   *   id: 1,
   *   category: 229,
   *   label: '5k',
   *   size: [...]
   * }
   */

  // ========================================
  // JERSEY OPTIONS
  // ========================================

  const sizes = useMemo(() => {
    const jerseyLimitItems = jerseyLimitData?.size || [];

    const normalized = normalizeLimitOptions(jerseyLimitItems);

    const baseOptions =
      normalized.length > 0 ? normalized : STATIC_SIZE_OPTIONS;

    return applyForcedSoldOut(baseOptions, soldOutSizes.size);
  }, [jerseyLimitData, soldOutSizes.size]);

  // ========================================
  // BELT OPTIONS
  // ========================================

  const beltSizes = useMemo(() => {
    const normalized = normalizeLimitOptions(beltLimitData);

    return applyForcedSoldOut(normalized, soldOutSizes.belt_size);
  }, [beltLimitData, soldOutSizes.belt_size]);

  // ========================================
  // FINISHER / JACKET OPTIONS
  // ========================================

  const jacketSizes = useMemo(() => {
    const normalized = normalizeLimitOptions(jacketLimitData);

    return applyForcedSoldOut(normalized, soldOutSizes.finisher_size);
  }, [jacketLimitData, soldOutSizes.finisher_size]);

  const sizeReady = sizes.length > 0;

  const sizeLoadFailed = !sizeReady;

  // ========================================
  // BIRTH DATE
  // ========================================

  const birthMaxFromServer = isoDateOnly(ev?.category?.maxAllowedDate);

  // ========================================
  // REGISTRATION CLOSE
  // ========================================

  const regClosed = ev?.category?.endReg
    ? new Date(ev.category.endReg) < new Date()
    : false;

  // ========================================
  // RACE CATEGORY
  // ========================================

  const rawRace =
    initialValues?.race_category ??
    state?.race_category ??
    ev?.category?.name ??
    normalizeRaceNameToEnum(ev?.category?.race_category) ??
    '10k';

  const normalizedRace = normalizeRaceNameToEnum(rawRace) || '10k';

  // ========================================
  // REACT HOOK FORM
  // ========================================

  const methods = useForm({
    resolver: async (values, ctx, opts) => {
      // ====================================
      // BIRTH VALIDATION
      // ====================================

      const birthMaxISO = birthMaxFromServer || '2011-12-31';

      const dynamicBirthSchema = z.object({
        brith_date: z
          .string()
          .min(1, 'Tanggal lahir wajib diisi')
          .refine(isISO_YMD, 'Format tanggal tidak valid (yyyy-mm-dd)')
          .refine((v) => toUTCDate(v) <= toUTCDate(birthMaxISO), {
            message: `Max brith date allowed is ${fmtDMY(birthMaxISO)}`,
          }),
      });

      // ====================================
      // REGION VALIDATION
      // ====================================

      const regionOptionSchema = z
        .union([
          z.object({
            label: z.string().min(1),
            value: z.string().min(1),
          }),

          z.string().min(1),
        ])
        .transform((value) =>
          typeof value === 'string'
            ? {
                label: value,
                value,
              }
            : value,
        );

      const dynamicRegionSchema = z
        .object({
          province: regionOptionSchema,
          city_regency: regionOptionSchema,
        })
        .superRefine((value, issueCtx) => {
          const provinceData = regionData.find(
            (item) => item.value === value.province.value,
          );

          if (!provinceData) {
            issueCtx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['province'],
              message: 'Province is not valid.',
            });

            return;
          }

          const cityRegencyValid = (provinceData.regencies || []).some(
            (item) => item.value === value.city_regency.value,
          );

          if (!cityRegencyValid) {
            issueCtx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['city_regency'],
              message: 'The city/regency does not match the selected province.',
            });
          }
        });

      // ====================================
      // BASE SCHEMA
      // ====================================

      const combined = registrationSchema
        .and(dynamicBirthSchema)
        .and(dynamicRegionSchema);

      const baseResolver = zodResolver(combined);

      const result = await baseResolver(values, ctx, opts);

      // ====================================
      // RAW SIZE VALIDATION
      // ====================================
      //
      // PENTING:
      // pakai `values`, BUKAN hasil parse registrationSchema
      //

      const jerseyError = validateSizeStock({
        value: values.size,
        options: sizes,
        required: true,
        requiredMessage: 'Select your jersey size',
        soldOutMessage: 'Size jersey ini sudah habis',
      });

      const beltError = validateSizeStock({
        value: values.belt_size,
        options: beltSizes,
        required: true,
        requiredMessage: 'Select your belt size',
        soldOutMessage: 'Size belt ini sudah habis',
      });

      const finisherError = validateSizeStock({
        value: values.finisher_size,
        options: jacketSizes,
        required: false,
        soldOutMessage: 'Size finisher ini sudah habis',
      });

      // ====================================
      // INJECT ERROR KE RHF
      // ====================================

      if (jerseyError) {
        result.errors.size = {
          type: 'custom',
          message: jerseyError,
        };
      }

      if (beltError) {
        result.errors.belt_size = {
          type: 'custom',
          message: beltError,
        };
      }

      if (finisherError) {
        result.errors.finisher_size = {
          type: 'custom',
          message: finisherError,
        };
      }

      return result;
    },

    defaultValues: {
      race_category: normalizedRace,

      email: '',
      repeat_email: '',

      name: '',
      contact: '',

      gender: null,
      nationality: null,

      address: null,

      province: null,
      city_regency: null,

      id_type: null,
      id_number: '',

      brith_date: '',

      blood_type: null,

      org_kom: '',
      org_com: '',

      rpt_10: undefined,
      rpt_21: undefined,

      exp_fin_time: {
        hour: 0,
        minute: 0,
        second: 0,
      },

      medical_note: '',

      emergency_person: '',
      emergency_contact: '',
      emergency_relation: null,

      size: '',
      belt_size: '',
      finisher_size: '',
    },

    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    setValue,
    resetField,
    getValues,
    trigger,
    setFocus,
    formState,
  } = methods;

  useEffect(() => {
    const fields = ['size', 'belt_size', 'finisher_size'];

    const firstErrorField = fields.find((field) => stockErrors[field]);

    fields.forEach((field) => {
      if (!stockErrors[field]) return;

      methods.setError(field, {
        type: 'manual',
        message: stockErrors[field],
      });
    });

    if (firstErrorField) {
      requestAnimationFrame(() => {
        setFocus(firstErrorField);
      });
    }
  }, [stockErrors, methods, setFocus]);

  // ========================================
  // PROVINCE
  // ========================================

  const selectedProvince = watch('province');

  const provinceOptions = useMemo(() => {
    return regionData.map((province) => ({
      label: province.label,

      value: province.value,
    }));
  }, [regionData]);

  // ========================================
  // CITY / REGENCY
  // ========================================

  const cityRegencyOptions = useMemo(() => {
    if (!selectedProvince?.value) {
      return [];
    }

    const province = regionData.find(
      (item) => item.value === selectedProvince.value,
    );

    return province?.regencies || [];
  }, [regionData, selectedProvince?.value]);

  const previousProvinceRef = useRef(null);

  useEffect(() => {
    const currentProvinceValue = selectedProvince?.value || null;

    if (previousProvinceRef.current === null) {
      previousProvinceRef.current = currentProvinceValue;

      return;
    }

    if (previousProvinceRef.current !== currentProvinceValue) {
      setValue('city_regency', null, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    previousProvinceRef.current = currentProvinceValue;
  }, [selectedProvince?.value, setValue]);

  // ========================================
  // PREFILL EDIT
  // ========================================

  useEffect(() => {
    if (!initialValues) return;

    methods.reset(initialValues, {
      keepDefaultValues: false,
    });
  }, [initialValues, methods]);

  // ========================================
  // SET RACE CATEGORY
  // ========================================

  useEffect(() => {
    setValue('race_category', normalizedRace, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [normalizedRace, setValue]);

  // ========================================
  // RESTORE JERSEY SIZE ON EDIT
  // ========================================

  useEffect(() => {
    const iv = initialValues?.size;

    if (!iv || !sizes?.length) {
      return;
    }

    const wanted = typeof iv === 'string' ? iv : iv.value;

    const opt = sizes.find((o) => o.value === wanted);

    if (opt) {
      methods.setValue('size', opt, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [sizes, initialValues, methods]);

  // ========================================
  // RESTORE BELT SIZE ON EDIT
  // ========================================

  useEffect(() => {
    const iv = initialValues?.belt_size;

    if (!iv || !beltSizes.length) {
      return;
    }

    const wanted = typeof iv === 'string' ? iv : iv.value;

    const opt = beltSizes.find((o) => o.value === wanted);

    if (opt) {
      methods.setValue('belt_size', opt, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [beltSizes, initialValues, methods]);

  // ========================================
  // RESTORE FINISHER SIZE ON EDIT
  // ========================================

  useEffect(() => {
    const iv = initialValues?.finisher_size;

    if (!iv || !jacketSizes.length) {
      return;
    }

    const wanted = typeof iv === 'string' ? iv : iv.value;

    const opt = jacketSizes.find((o) => o.value === wanted);

    if (opt) {
      methods.setValue('finisher_size', opt, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [jacketSizes, initialValues, methods]);

  // ========================================
  // PROTECT JERSEY VALUE
  // ========================================

  useEffect(() => {
    const cur = methods.getValues('size');

    const curVal = cur && typeof cur === 'object' ? cur.value : '';

    if (!curVal) return;

    const opt = sizes.find((o) => o.value === curVal);

    if (!opt || opt.disabled) {
      methods.resetField('size', {
        defaultValue: '',
      });

      if (methods.formState.errors.size) {
        methods.clearErrors('size');
      }
    }
  }, [categoryId, sizes, methods]);

  // ========================================
  // PROTECT BELT VALUE
  // ========================================

  useEffect(() => {
    const cur = methods.getValues('belt_size');

    const curVal = cur && typeof cur === 'object' ? cur.value : '';

    if (!curVal) return;

    const opt = beltSizes.find((o) => o.value === curVal);

    if (!opt || opt.disabled) {
      methods.resetField('belt_size', {
        defaultValue: '',
      });

      methods.clearErrors('belt_size');
    }
  }, [beltSizes, methods]);

  // ========================================
  // PROTECT FINISHER VALUE
  // ========================================

  useEffect(() => {
    const cur = methods.getValues('finisher_size');

    const curVal = cur && typeof cur === 'object' ? cur.value : '';

    if (!curVal) return;

    const opt = jacketSizes.find((o) => o.value === curVal);

    if (!opt || opt.disabled) {
      methods.resetField('finisher_size', {
        defaultValue: '',
      });

      methods.clearErrors('finisher_size');
    }
  }, [jacketSizes, methods]);

  // ========================================
  // REPEAT EMAIL VALIDATION
  // ========================================

  const email = watch('email');

  const repeatEmail = watch('repeat_email');

  useEffect(() => {
    const isEmail = (s) => /\S+@\S+\.\S+/.test(s || '');

    if (repeatEmail && isEmail(email)) {
      trigger('repeat_email');
    }
  }, [email, repeatEmail, trigger]);

  const values = watch();

  // ========================================
  // VISIBILITY
  // ========================================

  /**
   * finisher_size tidak perlu check category 231 di sini.
   *
   * runnerFormStructure sudah punya:
   *
   * visibleWhen: {
   *   field: 'race_category',
   *   equals: '21k'
   * }
   */

  const visibleRunner = useMemo(
    () => applyVisibility(runnerFormStructure, values),
    [values],
  );

  // ========================================
  // INJECT BIRTH + SIZE OPTIONS
  // ========================================

  const runnerWithBirthLimit = useMemo(() => {
    const base = !birthMaxFromServer
      ? visibleRunner
      : visibleRunner.map((f) =>
          f.key === 'brith_date'
            ? {
                ...f,

                min: f.min || '1900-01-01',

                max: birthMaxFromServer,
              }
            : f,
        );

    return base.map((f) => {
      // ================================
      // JERSEY
      // ================================

      if (f.key === 'size') {
        return {
          ...f,

          type: 'select',

          required: true,

          placeholder: sizeReady
            ? 'Select Jersey Size'
            : 'Jersey Size not Available Yet',

          options: sizes.map((o) => ({
            value: o.value,

            label: o.disabled
              ? `${o.label} (Out of Stock)`
              : `${o.label} (${o.remaining} left)`,

            disabled: !!o.disabled,
          })),

          disabled: !sizeReady,
        };
      }

      // ================================
      // BELT
      // ================================

      if (f.key === 'belt_size') {
        return {
          ...f,

          type: 'select',

          placeholder:
            beltSizes.length > 0
              ? 'Select Belt Size'
              : 'Belt Size not Available Yet',

          options: beltSizes.map((o) => ({
            value: o.value,

            label: o.disabled
              ? `${o.label} (Out of Stock)`
              : `${o.label} (${o.remaining} left)`,

            disabled: !!o.disabled,
          })),

          disabled: beltSizes.length === 0,
        };
      }

      // ================================
      // FINISHER
      // ================================

      if (f.key === 'finisher_size') {
        return {
          ...f,

          type: 'select',

          placeholder:
            jacketSizes.length > 0
              ? 'Select Jacket Size'
              : 'Jacket Size not Available Yet',

          options: jacketSizes.map((o) => ({
            value: o.value,

            label: o.disabled
              ? `${o.label} (Out of Stock)`
              : `${o.label} (${o.remaining} left)`,

            disabled: !!o.disabled,
          })),

          disabled: jacketSizes.length === 0,
        };
      }

      return f;
    });
  }, [
    visibleRunner,
    birthMaxFromServer,
    sizes,
    sizeReady,
    beltSizes,
    jacketSizes,
  ]);

  // ========================================
  // LOCATION OPTIONS
  // ========================================

  const runnerWithLocationOptions = useMemo(() => {
    return runnerWithBirthLimit.map((field) => {
      if (field.key === 'province') {
        return {
          ...field,

          options: provinceOptions,

          disabled: regionsLoading,

          placeholder: regionsLoading
            ? 'Memuat Provinsi...'
            : 'Pilih Provinsi...',
        };
      }

      if (field.key === 'city_regency') {
        return {
          ...field,

          options: cityRegencyOptions,

          disabled:
            regionsLoading ||
            !selectedProvince?.value ||
            cityRegencyOptions.length === 0,

          placeholder: regionsLoading
            ? 'Memuat Kota / Kabupaten...'
            : !selectedProvince?.value
              ? 'Pilih Provinsi terlebih dahulu'
              : 'Pilih Kota / Kabupaten...',
        };
      }

      return field;
    });
  }, [
    runnerWithBirthLimit,
    provinceOptions,
    cityRegencyOptions,
    regionsLoading,
    selectedProvince?.value,
  ]);

  // ========================================
  // RESET HIDDEN FIELD
  // ========================================

  useEffect(() => {
    const visibleKeys = new Set(
      runnerWithLocationOptions.map((field) => field.key),
    );

    runnerFormStructure.forEach((field) => {
      if (!visibleKeys.has(field.key) && getValues(field.key) !== undefined) {
        resetField(field.key, {
          defaultValue: undefined,
        });
      }
    });
  }, [runnerWithLocationOptions, getValues, resetField]);

  // ========================================
  // SUBMIT
  // ========================================

  const onSubmit = (data) => {
    const rawValues = getValues();

    const finalValues = {
      ...data,

      size: rawValues.size,
      belt_size: rawValues.belt_size,
      finisher_size: rawValues.finisher_size,
    };

    const nextMeta = {
      // IMPORTANT:
      // preserve soldOutSizes + meta lainnya
      ...(meta || {}),

      eventId,

      coupon: meta?.coupon ?? ev?.coupon ?? null,

      category_id: categoryId,

      jerseyLimit: jerseyLimitData,
      beltLimit: beltLimitData,
      jacketLimit: jacketLimitData,

      // error popup lama tidak perlu ikut ke Summary
      stockErrors: {},
    };

    navigate(SUMMARY_PATH, {
      replace: true,

      state: {
        values: finalValues,
        meta: nextMeta,
      },
    });
  };

  // ========================================
  // PRE SUBMIT
  // ========================================

  const onPreSubmit = (data) => {
    if (!sizeReady) {
      methods.setError('size', {
        type: 'manual',

        message: 'Ukuran jersey belum berhasil dimuat. Silakan coba lagi.',
      });

      setFocus('size');

      return;
    }

    onSubmit(data);
  };

  // ========================================
  // INVALID FORM
  // ========================================

  const onInvalid = (errors) => {
    const first = Object.keys(errors || {})[0];

    if (first) {
      setFocus(first);
    }
  };

  // ========================================
  // SIZE CHART
  // ========================================

  const handleSizeChartOpen = (key) => {
    setSelectedChart(key);

    setSizeChartOpen(true);
  };

  const showDecodeError = !!decodeError && !initialValues && !meta;

  // ========================================
  // RENDER
  // ========================================

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '56rem',
          mx: 'auto',

          px: {
            xs: 2,
            md: 0,
          },

          mt: {
            xs: 3,
            md: 5,
            xl: 8,
          },

          py: {
            xs: 5,
            sm: 3,
            md: 0,
          },
        }}
      >
        {showDecodeError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {decodeError}
          </Alert>
        ) : null}

        {regionsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load province and city/regency data. Please refresh the
            page.
          </Alert>
        ) : null}

        {sizeLoadFailed ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Gagal memuat daftar size. Silakan buka halaman event ulang.
          </Alert>
        ) : null}

        {/* =================================
            CATEGORY + COUPON
        ================================= */}

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',

              alignItems: 'center',

              gap: 1.25,

              border: '3px solid',

              borderColor: SECTION_BORDER,

              borderRadius: `${RADIUS_PX}px`,

              px: 2,
              py: 1,

              width: 'fit-content',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,

                color: 'text.secondary',
              }}
            >
              KATEGORI
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,

                textTransform: 'uppercase',
              }}
            >
              {ev?.category?.name || values.race_category || '-'}
            </Typography>
          </Box>

          {(meta?.coupon || ev?.coupon)?.voucher ? (
            <Box
              sx={{
                display: 'inline-flex',

                alignItems: 'center',

                gap: 1,

                border: '3px dashed',

                borderColor: SECTION_BORDER,

                borderRadius: `${RADIUS_PX}px`,

                px: 2,
                py: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                }}
              >
                Voucher:
              </Typography>

              <Typography variant="body2">
                {(meta?.coupon || ev?.coupon)?.voucher}
              </Typography>
            </Box>
          ) : null}
        </Box>

        {/* =================================
            FORM
        ================================= */}

        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onPreSubmit, onInvalid)}
          sx={{
            display: 'flex',

            flexDirection: 'column',

            gap: 3,
          }}
        >
          <Stack spacing={3}>
            {/* =============================
                EMAIL
            ============================= */}

            <Section title="Email Info">
              <Grid container spacing={2}>
                {emailFormStructure.map((f) => (
                  <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                    <Field field={f} />
                  </Grid>
                ))}
              </Grid>
            </Section>

            {/* =============================
                RUNNER
            ============================= */}

            <Section title="Runner Info">
              <Grid container spacing={2}>
                {runnerWithLocationOptions.map((f) =>
                  f.key === 'size' ||
                  f.key === 'belt_size' ||
                  f.key === 'finisher_size' ? (
                    <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                      <Box
                        sx={{
                          display: 'flex',

                          alignItems: 'flex-end',

                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                          }}
                        >
                          <Field field={f} />
                        </Box>

                        <Button
                          variant="outlined"
                          onClick={() => handleSizeChartOpen(f.key)}
                          sx={{
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {f.key === 'size'
                            ? 'Race Appeal'
                            : f.key === 'belt_size'
                              ? 'Belt Appeal'
                              : 'Jacket Appeal'}
                        </Button>
                      </Box>
                    </Grid>
                  ) : (
                    <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                      <Field field={f} />
                    </Grid>
                  ),
                )}
              </Grid>
            </Section>

            {/* =============================
                EMERGENCY
            ============================= */}

            <Section title="Emergency Info">
              <Grid container spacing={2}>
                {emergencyFormStructure.map((f) => (
                  <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                    <Field field={f} />
                  </Grid>
                ))}
              </Grid>
            </Section>
          </Stack>

          {/* ===============================
              SUBMIT
          =============================== */}

          <Box
            sx={{
              pt: 1,

              display: 'flex',

              justifyContent: 'space-between',

              alignItems: 'center',
            }}
          >
            {formState.isSubmitted && !formState.isValid ? (
              <Typography
                sx={{
                  color: '#d32f2f',
                }}
              >
                Please fix the highlighted fields.
              </Typography>
            ) : (
              <span />
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={
                regClosed || !sizeReady || regionsLoading || !!regionsError
              }
              sx={{
                bgcolor: '#d32f2f',

                '&:hover': {
                  bgcolor: '#b71c1c',
                },

                borderRadius: 1,

                px: 5,

                py: 1.25,

                fontWeight: 700,

                textTransform: 'uppercase',

                letterSpacing: 0.5,
              }}
            >
              {regClosed
                ? 'Pendaftaran Ditutup'
                : !sizeReady
                  ? 'Size Belum Siap'
                  : 'Go To Summary'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* =================================
          SIZE CHART DIALOG
      ================================= */}

      <SizeChartDialog
        open={sizeChartOpen}
        onClose={() => setSizeChartOpen(false)}
        imgSrc={
          selectedChart === 'size'
            ? sizeChartImgSrc
            : selectedChart === 'belt_size'
              ? beltChartImgSrc
              : jacketChartImgSrc
        }
        title={`${
          selectedChart === 'size'
            ? 'Race'
            : selectedChart === 'belt_size'
              ? 'Belt'
              : 'Jacket'
        } Appeal — Size Chart`}
      />

      <RegStatusDialog
        open={stockDialog.open}
        status="error"
        title={stockDialog.title}
        message={stockDialog.message}
        buttonLabel="Choose Another Size"
        onClose={() => {
          setStockDialog({
            open: false,
            title: '',
            message: '',
          });
        }}
      />
    </FormProvider>
  );
}
